import axios from 'axios';
import client from './client';
import { API_URL, USE_MOCKS } from './config';

async function loadAuthMocks() {
    return import('./mocks/auth');
}

async function loadAdminMocks() {
    return import('./mocks/admin');
}

export async function adminLogin(login, password) {
    if (USE_MOCKS) {
        const authMock = await loadAuthMocks();
        return authMock.mockAdminLogin(login, password);
    }

    const { data } = await client.post('/auth/admin/login', {
        login: login.trim(),
        password,
    }, { skipAuthRefresh: true });
    return data;
}

export async function logout() {
    if (USE_MOCKS) {
        const authMock = await loadAuthMocks();
        return authMock.mockLogout();
    }

    const { data } = await client.post('/auth/logout');
    return data;
}

export async function getDashboard(periodDays = 30) {
    if (USE_MOCKS) {
        const adminMock = await loadAdminMocks();
        return adminMock.mockGetDashboard(periodDays);
    }

    const { data } = await client.get('/api/admin/dashboard', {
        params: { period_days: periodDays },
    });
    return data;
}

export async function listResource(resource, params = {}) {
    if (USE_MOCKS) {
        const adminMock = await loadAdminMocks();
        return adminMock.mockListResource(resource, params);
    }

    const { data } = await client.get(`/api/admin/${resource}`, { params });
    return data;
}

export async function getResource(resource, id) {
    if (USE_MOCKS) {
        const adminMock = await loadAdminMocks();
        return adminMock.mockGetResource(resource, id);
    }

    const { data } = await client.get(`/api/admin/${resource}/${id}`);
    return data;
}

export async function createResource(resource, body) {
    if (USE_MOCKS) {
        const adminMock = await loadAdminMocks();
        return adminMock.mockCreateResource(resource, body);
    }

    const { data } = await client.post(`/api/admin/${resource}`, body);
    return data;
}

export async function updateResource(resource, id, body) {
    if (USE_MOCKS) {
        const adminMock = await loadAdminMocks();
        return adminMock.mockUpdateResource(resource, id, body);
    }

    const { data } = await client.patch(`/api/admin/${resource}/${id}`, body);
    return data;
}

export async function deleteResource(resource, id) {
    if (USE_MOCKS) {
        const adminMock = await loadAdminMocks();
        return adminMock.mockDeleteResource(resource, id);
    }

    await client.delete(`/api/admin/${resource}/${id}`);
}

export async function extendSubscription(id, days) {
    if (USE_MOCKS) {
        const adminMock = await loadAdminMocks();
        return adminMock.mockExtendSubscription(id, days);
    }

    const { data } = await client.post(`/api/admin/subscriptions/${id}/extend`, { days });
    return data;
}

export async function listMetrics(params = {}) {
    if (USE_MOCKS) {
        const adminMock = await loadAdminMocks();
        return adminMock.mockListMetrics(params);
    }

    const { data } = await client.get('/api/admin/metrics', { params });
    return data;
}

export async function getMetricsSummary(params = {}) {
    if (USE_MOCKS) {
        const adminMock = await loadAdminMocks();
        return adminMock.mockGetMetricsSummary(params);
    }

    const { data } = await client.get('/api/admin/metrics/summary', { params });
    return data;
}

export async function getPricingAnchors() {
    if (USE_MOCKS) {
        const adminMock = await loadAdminMocks();
        return adminMock.mockGetPricingAnchors();
    }

    const { data } = await client.get('/api/admin/pricing/anchors');
    return data;
}

export async function putPricingAnchors(anchors) {
    if (USE_MOCKS) {
        const adminMock = await loadAdminMocks();
        return adminMock.mockPutPricingAnchors(anchors);
    }

    const { data } = await client.put('/api/admin/pricing/anchors', { anchors });
    return data;
}

export async function getSiteStatus() {
    if (USE_MOCKS) {
        const adminMock = await loadAdminMocks();
        return adminMock.mockGetSiteStatus();
    }

    const { data } = await axios.get(`${API_URL}/site/status`, {
        headers: { Accept: 'application/json' },
    });
    return data;
}

export async function updateSiteStatus(body) {
    if (USE_MOCKS) {
        const adminMock = await loadAdminMocks();
        return adminMock.mockUpdateSiteStatus(body);
    }

    const { data } = await client.patch('/api/admin/site-status', body);
    return data;
}

export async function listReferralTransactions(params = {}) {
    if (USE_MOCKS) {
        const adminMock = await loadAdminMocks();
        return adminMock.mockListReferralTransactions(params);
    }

    const { data } = await client.get('/api/admin/referral-transactions', { params });
    return data;
}

export async function searchCustomers(query, limit = 10) {
    if (USE_MOCKS) {
        const adminMock = await loadAdminMocks();
        return adminMock.mockSearchCustomers(query, limit);
    }

    const { data } = await client.get('/api/admin/customers/search', {
        params: { q: query, limit },
    });
    return data;
}
